import React from "react";
import * as M from "../../Modal";
import * as H from "../../../hooks";
import * as C from "../../../Common";
import * as BS from "react-bootstrap";
import * as S from "../../../services";
import { FP, T, TB, TC } from "../../../Constants";

export type KPIEditorProps = {
    /** The type of mission */
    mission_type: T.Mission["type"];
    /** The mission asset id */
    asset: T.Mission["asset"];
    /** The mission id */
    mission_id: T.Mission["_id"];
    /** Show the form in a modal */
    popup?: boolean;
    /** Extra Modal styles */
    modal?: M.StyleModalProps;
    /** Callback for the validation of the modal */
    on_validate: (params: Record<"kpis", KPIs[]> & Record<"date", string>) => void;
}

/** The KPI properties names */
type KPI_Props = keyof ReturnType<T.API.Utils.Missions.GetRegKPIs>[number]["kpi"]
    | keyof ReturnType<T.API.Utils.Missions.GetMissionRemsKPIs>[number]["kpi"];
/** The merger between the reg and rems KPIs */
type KPIs = T.MakePartialProp<ReturnType<T.API.Utils.Missions.GetRegKPIs>[number] & ReturnType<T.API.Utils.Missions.GetMissionRemsKPIs>[number], "kpi">;
/** A collection of errors for the form */
type Errors = T.Errors<Record<KPI_Props, string[]> & Record<"date", string>>;
/** The mission types that can have KPIs */
const KPI_Mission_Types = ["cdm", "edl_tech", "edl_reg"] as T.Mission["type"][];
/** The labels for the KPIs */
const KPI_labels = {
    "kpi_conform": TC.KPIS_EDITOR_REG_CONFORM,
    "kpi_presence": TC.KPIS_EDITOR_REG_PRESENCE,
    "nb_low_crit": TC.KPIS_EDITOR_REMS_LOW,
    "nb_mid_crit": TC.KPIS_EDITOR_REMS_MID,
    "nb_high_crit": TC.KPIS_EDITOR_REMS_HIGH,
} as Record<KPI_Props, string>;

/** The order of the KPIs */
const KPI_order = {
    "kpi_conform": 0,
    "kpi_presence": 1,
    "nb_high_crit": 2,
    "nb_mid_crit": 3,
    "nb_low_crit": 4,
} as Record<KPI_Props, number>;

const sort_kpis = (kpis: KPIs) => Object.entries(kpis.kpi).sort((a, b) => KPI_order[a[0] as KPI_Props] - KPI_order[b[0] as KPI_Props]);

const KPIEditor: React.FC<KPIEditorProps> = ({ on_validate, ...props }) => {
    const lg = H.useLanguage();
    const [auth] = H.useAuth();
    const [errors, set_errors] = React.useState<Errors>({});
    const [kpis, set_kpis, status] = H.useAsyncState<KPIs[]>([]);
    const [date, set_date] = React.useState<string>(new Date().toISOString());

    React.useEffect(() => {
        let isSubscribed = true;
        const is_rem_kpi = props.mission_type === "cdm" || props.mission_type === "edl_tech";

        if (auth.userId) {
            if (is_rem_kpi) S.getMissionRemsKPIs(props.mission_id)
                .then(res => isSubscribed && set_kpis(res.data, "done"))
                .catch(() => isSubscribed && set_kpis([], "error"));
            else if (props.mission_type === "edl_reg") S.getRegKPIs({ context: { roots: props.asset }, user: auth.userId })
                .then(res => isSubscribed && set_kpis(res.data, "done"))
                .catch(() => isSubscribed && set_kpis([], "error"));
            else set_kpis([], "done");
        }

        return () => {
            isSubscribed = false;
            set_kpis([], "load");
        };
    }, [set_kpis, props.mission_type, props.mission_id, props.asset, auth.userId]);

    const validate = React.useCallback(() => {
        const new_errors = {} as Errors;
        const date_obj = TB.getDate(date);

        if (!date_obj) new_errors.date = TC.GLOBAL_REQUIRED_FIELD;

        for (let i = 0; i < kpis.length; i++) {
            const kpi = kpis[i];
            for (const [key, value] of Object.entries(kpi.kpi)) {
                let error = "";
                if (typeof value !== "number") error = TC.GLOBAL_REQUIRED_FIELD;
                else if (isNaN(value)) error = TC.GLOBAL_REQUIRED_FIELD;
                else if (value < 0) error = TC.GLOBAL_NUMBER_MUST_BE_POSITIVE;
                // Here the value must be a percentage
                else if (props.mission_type === "edl_reg") {
                    if (value > 100) error = TC.APS_ERR_PCT_OVER;
                }

                if (error) {
                    if (!Array.isArray(new_errors[key])) new_errors[key] = [];
                    new_errors[key][i] = error;
                }
            }
        }

        if (Object.keys(new_errors).length > 0) set_errors(new_errors);
        else on_validate?.({ kpis, date });
    }, [date, kpis, on_validate, props.asset]);

    const footer = React.useMemo(() => <div>
        <C.Button variant="danger" onClick={() => on_validate?.(null)} text={TC.GLOBAL_CANCEL} />
        <C.Button variant="primary" onClick={validate} text={TC.GLOBAL_CONFIRM} className="ms-2" />
    </div>, [validate, on_validate]);

    return React.createElement(
        props.popup ? M.BlankModal : React.Fragment,
        props.popup ? {
            ...props.modal,
            footer: footer,
            size: props.modal?.size || "md",
            onQuit: () => on_validate?.(null),
            title: props.modal?.title || TC.REPORT_FINAL_VERSION,
        } as M.BlankModalProps : null,
        <C.Spinner status={status} min_load_size="200px">
            {lg.getStaticText(TC.REPORT_FINAL_REPORT_CONFIRM)}

            {KPI_Mission_Types.includes(props.mission_type) && <BS.Accordion className="mt-3">

                <BS.Accordion.Item eventKey="kpi">
                    <BS.Accordion.Header>
                        {lg.getStaticText(TC.KPIS_EDITOR_ADVANCED_OPTIONS)}
                    </BS.Accordion.Header>
                    <BS.Accordion.Body>

                        <C.Form.DateTime
                            enableTime
                            value={date}
                            label={TC.DATE}
                            error={errors.date}
                            onChange={set_date}
                            labelPosition="left"
                        />

                        {kpis.map((kpi, index) => <div key={kpi.id}>
                            <C.Title level={4} text={kpi.name} />

                            {sort_kpis(kpi).map(([key, value]) => <C.Form.NumField
                                key={key}
                                value={value}
                                decimalLimit={0}
                                labelPosition="left"
                                label={KPI_labels[key]}
                                error={errors?.[key as KPI_Props]?.[index]}
                                suffix={props.mission_type === "edl_reg" ? "%" : undefined}
                                onChange={v => set_kpis(p => p.map(k => k.id === kpi.id ? { ...k, kpi: { ...k.kpi, [key]: v } } : k))}
                            />)}
                        </div>)}

                    </BS.Accordion.Body>
                </BS.Accordion.Item>

            </BS.Accordion>}

            {!props.popup && <C.Flex justifyContent="end" children={footer} />}

        </C.Spinner>
    );
};

export default KPIEditor;